import { DestroyRef, Injectable, computed, effect, inject, signal, untracked } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs';

import { GuideTarget, setupGuideStep } from './setup-guide.config';
import { SetupGuideService } from './setup-guide.service';

export const GUIDE_QUERY_PARAM = 'guide';

export type GuideWalkthrough = {
  stepId: string;
  targets: readonly GuideTarget[];
  index: number;
};

/** Drives the pointer walkthrough. Started by a `?guide=<stepId>` link; the overlay component renders it. */
@Injectable({ providedIn: 'root' })
export class GuideSpotlightService {
  private readonly router = inject(Router);
  private readonly setupGuide = inject(SetupGuideService);

  readonly walkthrough = signal<GuideWalkthrough | null>(null);
  /** Tip shown as a toast when the control never appeared on the page. */
  readonly fallbackTip = signal<string | null>(null);
  readonly currentTarget = computed(() => {
    const walkthrough = this.walkthrough();
    return walkthrough ? walkthrough.targets[walkthrough.index] ?? null : null;
  });
  readonly isLast = computed(() => {
    const walkthrough = this.walkthrough();
    return !!walkthrough && walkthrough.index >= walkthrough.targets.length - 1;
  });

  /** The `guide` value of the previous navigation: pages that merge query params carry it along, which must not restart. */
  private previousGuideParam: string | null = null;
  private routerConnected = false;
  /** A guide link opened before the workspace permissions arrived (deep link, reload). */
  private readonly pendingStepId = signal<string | null>(null);
  private readonly validatedStepId = signal<string | null>(null);

  constructor() {
    effect(() => {
      const eligible = this.setupGuide.availableSteps();
      const active = this.validatedStepId();
      if (active && !eligible.some(step => step.id === active)) untracked(() => this.close());
      const stepId = this.pendingStepId();
      if (!stepId || !this.setupGuide.canUse()) return;
      if (this.setupGuide.planPending()) {
        untracked(() => void this.setupGuide.ensurePlan());
        return;
      }
      untracked(() => {
        this.pendingStepId.set(null);
        this.startStep(stepId);
      });
    });
  }

  /** Called once by the overlay mounted in the shell. */
  connectRouter(destroyRef: DestroyRef): void {
    if (this.routerConnected) return;
    this.routerConnected = true;
    destroyRef.onDestroy(() => (this.routerConnected = false));
    this.handleUrl(this.router.url);
    this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd), takeUntilDestroyed(destroyRef))
      .subscribe(event => this.handleUrl(event.urlAfterRedirects));
  }

  startStep(stepId: string): boolean {
    const step = setupGuideStep(stepId);
    if (!step || !step.targets.length) return false;
    if (!this.setupGuide.canUse() || this.setupGuide.planPending()) {
      this.pendingStepId.set(step.id);
      if (this.setupGuide.canUse()) void this.setupGuide.ensurePlan();
      return false;
    }
    if (!this.setupGuide.availableSteps().some(candidate => candidate.id === step.id)) return false;
    this.start(step.id, step.targets);
    this.validatedStepId.set(step.id);
    return true;
  }

  start(stepId: string, targets: readonly GuideTarget[]): void {
    if (!targets.length) return;
    this.validatedStepId.set(null);
    this.fallbackTip.set(null);
    this.walkthrough.set({ stepId, targets, index: 0 });
  }

  next(): void {
    const walkthrough = this.walkthrough();
    if (!walkthrough) return;
    if (walkthrough.index >= walkthrough.targets.length - 1) {
      this.close();
      return;
    }
    this.walkthrough.set({ ...walkthrough, index: walkthrough.index + 1 });
  }

  close(): void {
    this.walkthrough.set(null);
    this.validatedStepId.set(null);
    this.pendingStepId.set(null);
  }

  /** The target never rendered: end quietly and keep the advice as a toast. */
  fallBack(tipKey: string): void {
    this.close();
    this.fallbackTip.set(tipKey);
  }

  dismissFallback(): void {
    this.fallbackTip.set(null);
  }

  private handleUrl(url: string): void {
    if (!url) return;
    const stepId = this.router.parseUrl(url).queryParamMap.get(GUIDE_QUERY_PARAM);
    const previous = this.previousGuideParam;
    this.previousGuideParam = stepId;
    if (!stepId) {
      this.pendingStepId.set(null);
      if (this.walkthrough()) this.close();
      return;
    }
    if (stepId !== previous) {
      this.close();
      this.startStep(stepId);
    }
  }
}
